<template>
    <div ref="rootRef" class="cal-month-picker">
        <button
            type="button"
            class="cal-month-picker__trigger"
            :aria-expanded="open"
            aria-haspopup="dialog"
            :disabled="disabled"
            @click.stop="toggle"
        >
            {{ label }}
        </button>

        <div
            v-if="open"
            class="cal-month-picker__popover"
            role="dialog"
            aria-label="Выбор месяца и года"
            @click.stop
        >
            <div class="cal-month-picker__year-row">
                <button
                    type="button"
                    class="cal-month-picker__year-btn"
                    aria-label="Предыдущий год"
                    @click="draftYear -= 1"
                >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <span class="cal-month-picker__year">{{ draftYear }}</span>
                <button
                    type="button"
                    class="cal-month-picker__year-btn"
                    aria-label="Следующий год"
                    @click="draftYear += 1"
                >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>

            <div class="cal-month-picker__months" role="listbox">
                <button
                    v-for="(monthLabel, index) in monthLabels"
                    :key="monthLabel"
                    type="button"
                    class="cal-month-picker__month"
                    :class="{
                        'is-selected': draftYear === year && index + 1 === month,
                    }"
                    role="option"
                    :aria-selected="draftYear === year && index + 1 === month"
                    @click="selectMonth(index + 1)"
                >
                    {{ monthLabel }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
    year: { type: Number, required: true },
    month: { type: Number, required: true },
    label: { type: String, required: true },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const monthLabels = [
    'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
    'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];

const rootRef = ref(null);
const open = ref(false);
const draftYear = ref(props.year);

watch(
    () => props.year,
    (next) => {
        if (!open.value) {
            draftYear.value = next;
        }
    },
);

function toggle() {
    if (props.disabled) {
        return;
    }
    open.value = !open.value;
    if (open.value) {
        draftYear.value = props.year;
    }
}

function close() {
    open.value = false;
}

function selectMonth(month) {
    emit('select', { year: draftYear.value, month });
    close();
}

function handleClickOutside(event) {
    if (rootRef.value && !rootRef.value.contains(event.target)) {
        close();
    }
}

function handleKeydown(event) {
    if (event.key === 'Escape' && open.value) {
        close();
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.cal-month-picker {
    position: relative;
    min-width: 10rem;
}

.cal-month-picker__trigger {
    display: block;
    width: 100%;
    margin: 0;
    padding: 0.25rem 0.5rem;
    border: none;
    border-radius: $radius-sm;
    background: transparent;
    text-align: center;
    font-size: 1.15rem;
    font-weight: 700;
    color: $color-brand-dark;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled),
    &[aria-expanded='true'] {
        background: $color-brand-alpha-soft;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.cal-month-picker__popover {
    position: absolute;
    top: calc(100% + 0.4rem);
    left: 50%;
    z-index: 20;
    width: 17rem;
    padding: 0.75rem;
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: $radius-md;
    background: #fff;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
    transform: translateX(-50%);
}

.cal-month-picker__year-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.65rem;
}

.cal-month-picker__year {
    font-size: 1rem;
    font-weight: 700;
    color: $color-brand-dark;
}

.cal-month-picker__year-btn {
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $color-brand-border;
    border-radius: $radius-sm;
    background: #fff;
    color: $color-brand;
    cursor: pointer;

    &:hover {
        background: $color-brand-alpha-soft;
        border-color: $color-brand;
    }
}

.cal-month-picker__months {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.35rem;
}

.cal-month-picker__month {
    padding: 0.55rem 0.25rem;
    border: 1px solid transparent;
    border-radius: $radius-sm;
    background: transparent;
    color: $color-text-primary;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: $color-brand-alpha-soft;
        color: $color-brand;
    }

    &.is-selected {
        border-color: $color-brand;
        background: $color-brand;
        color: #fff;
    }
}

@include down($bp-md) {
    .cal-month-picker {
        min-width: 8rem;
    }

    .cal-month-picker__trigger {
        font-size: 1rem;
    }
}
</style>
